import type { ReportListItem } from '../../../utils/reportsClient'

export const WPS_AGGREGATED_GROUP_ID = '__aggregated__'

/** @deprecated Bevel-scoped grouping removed; kept for test imports. */
export interface WpsBevelOption {
  bevelId: string
  bevelLabel: string
  defaultWpsNumber?: string
}

export interface WpsHistoryReport extends ReportListItem {
  isLatest: boolean
  isSynthetic?: boolean
}

export interface WpsHistoryWpsGroup {
  wpsNumber: string
  reports: WpsHistoryReport[]
}

export interface GroupedWpsHistory {
  wpsGroups: WpsHistoryWpsGroup[]
}

function isLive(report: ReportListItem): boolean {
  return !(typeof report.deletedAt === 'string' && report.deletedAt.trim().length > 0)
}

function asRevNumber(rev: string | undefined): number {
  if (typeof rev !== 'string') return 0
  const t = rev.trim()
  if (!/^\d+$/.test(t)) return 0
  return parseInt(t, 10)
}

function maxRevNumber(items: ReportListItem[]): number {
  let max = -1
  for (const r of items) {
    const n = asRevNumber(r.revision)
    if (n > max) max = n
  }
  return max
}

function reportWeldIds(r: ReportListItem): string[] {
  if (Array.isArray(r.weldIds) && r.weldIds.length > 0) {
    return r.weldIds.map((s) => s.trim()).filter(Boolean)
  }
  if (typeof r.weldId === 'string' && r.weldId.trim()) {
    return [r.weldId.trim()]
  }
  return []
}

function reportPqrNumbers(r: ReportListItem): string[] {
  if (Array.isArray(r.pqrNumbers) && r.pqrNumbers.length > 0) {
    return r.pqrNumbers.map((s) => s.trim()).filter(Boolean)
  }
  return []
}

/**
 * UI-only next-version placeholder. Never written to the DB.
 */
function buildSyntheticPlaceholderForTrain(
  wpsNumber: string,
  maxRev: number,
  scopeFrom: ReportListItem
): WpsHistoryReport {
  const weldIds = reportWeldIds(scopeFrom)
  const pqrNumbers = reportPqrNumbers(scopeFrom)
  return {
    id: `synth-${wpsNumber}`,
    name: wpsNumber,
    tag: 'WPS',
    fileKey: '',
    uploadedAt: '',
    wpsNumber,
    revision: String(maxRev + 1),
    weldIds: weldIds.length > 0 ? weldIds : undefined,
    weldId: weldIds[0],
    pqrNumbers: pqrNumbers.length > 0 ? pqrNumbers : undefined,
    bevelId: scopeFrom.bevelId,
    isLatest: true,
    isSynthetic: true
  }
}

function buildWpsGroups(forPipeSet: ReportListItem[]): WpsHistoryWpsGroup[] {
  const byWpsNumber = new Map<string, ReportListItem[]>()
  for (const r of forPipeSet) {
    const key =
      typeof r.wpsNumber === 'string' && r.wpsNumber.length > 0 ? r.wpsNumber : '(unknown)'
    const arr = byWpsNumber.get(key) ?? []
    arr.push(r)
    byWpsNumber.set(key, arr)
  }
  const groups: WpsHistoryWpsGroup[] = []
  Array.from(byWpsNumber.entries()).forEach(([wpsNumber, items]) => {
    const sorted = [...items].sort((a, b) => asRevNumber(b.revision) - asRevNumber(a.revision))
    const realReports: WpsHistoryReport[] = sorted.map((r) => ({
      ...r,
      isLatest: false
    }))
    const synth = buildSyntheticPlaceholderForTrain(
      wpsNumber,
      maxRevNumber(items),
      sorted[0]
    )
    groups.push({ wpsNumber, reports: [synth, ...realReports] })
  })
  groups.sort((a, b) => {
    const realA = a.reports[1]
    const realB = b.reports[1]
    const aLatest = realA?.uploadedAt || ''
    const bLatest = realB?.uploadedAt || ''
    return bLatest.localeCompare(aLatest)
  })
  return groups
}

/**
 * Group WPS reports by WPS number (flat). Each train is prepended with a
 * synthetic next-version placeholder; real revisions sort desc beneath it.
 */
export function groupWpsReportsForHistory(reports: ReportListItem[]): GroupedWpsHistory {
  const live = reports.filter(isLive)
  return { wpsGroups: buildWpsGroups(live) }
}
